<template>
    <UiModal
        :is-open="showEditUsageModal"
        title="Record Benefit Usage"
        width="600px"
        @close="closeModal"
    >
        <template #subtitle>
            <div class="flex items-center gap-2 text-sm text-slate-500">
                {{ selectedBenefit?.benefit?.benefit_title }}
                <span class="text-xs">•</span>

                <div class="flex items-center gap-2">
                    <ImageLoader>
                        <template #image>
                            <img
                                :src="`${cdnUrl}/${selectedBenefit?.card?.image?.path}/_small/${selectedBenefit?.card?.image?.file_name}`"
                                :alt="selectedBenefit?.card?.title"
                                class="w-8 h-5 rounded-sm"
                            />
                        </template>
                        <template #fallback>
                            <div class="w-8 h-5 bg-slate-200 rounded-sm"></div>
                        </template>
                    </ImageLoader>
                    <span>
                        {{ selectedBenefit?.card?.title }} ({{
                            selectedBenefit?.connected_account_last_four
                        }})
                    </span>
                </div>
            </div>
        </template>
        <template #content>
            <div class="w-full">
                <BenefitsUsageForm
                    v-model="model"
                    :errors="errors"
                    :is-count-based="isCountBased"
                    :count-label="countLabel"
                />

                <!-- Remaining Display -->
                <div class="mt-4">
                    <div
                        class="text-slate-500 text-sm"
                        v-if="
                            (isCountBased || isByCountValueBenefit) &&
                            remainingCount > 0
                        "
                    >
                        {{ remainingCount }} remaining
                    </div>
                    <div
                        class="text-slate-500 text-sm"
                        v-else-if="
                            !isCountBased &&
                            !isByCountValueBenefit &&
                            parseFloat(remainingAmount) > 0
                        "
                    >
                        ${{ remainingAmount }} remaining
                    </div>
                    <div v-else class="text-green-600 text-sm font-medium">
                        Fully redeemed!
                    </div>
                </div>
            </div>
        </template>

        <template #footer>
            <div class="flex justify-end space-x-3 py-4">
                <button
                    type="button"
                    class="btn-base btn-white"
                    @click="closeModal"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    class="btn-base btn-dark"
                    :disabled="loading"
                    @click="handleSave"
                >
                    <span v-if="loading">Saving...</span>
                    <span v-else>
                        {{ isEditing ? 'Update Usage' : 'Save Usage' }}
                    </span>
                </button>
            </div>
        </template>
    </UiModal>
</template>

<script setup>
import { ref, computed } from 'vue';
import dayjs from 'dayjs';
import { storeToRefs } from 'pinia';
import { useCardBenefitsStore } from '@/store/card-benefits';
import UiModal from '@/components/ui/modal/ui-modal.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import BenefitsUsageForm from '@/components/card-benefits/benefits-usage-form.vue';
import { notify } from 'notiwind';

const props = defineProps({
    editingEntry: {
        type: Object,
        default: null,
    },
    showEditUsageModal: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['usage-saved', 'close']);

const model = defineModel({
    type: Object,
    default: () => ({ amount: '', count: 1, date: new Date() }),
});

// CDN URL for images
const cdnUrl = computed(() => {
    return window.cdnUrl;
});

const store = useCardBenefitsStore();
const { selectedBenefit } = storeToRefs(store);

// Loading state
const loading = ref(false);

// Validation errors
const errors = ref({});

// Check if we're editing an existing entry
const isEditing = computed(() => !!props.editingEntry);

// Validation computed properties
const maxAmount = computed(() => {
    return selectedBenefit.value?.benefit?.amount
        ? parseFloat(selectedBenefit.value.benefit.amount)
        : null;
});

// The sub-benefit that owns the entry being edited, when it is one. The modal
// opens on the parent benefit, but a count-based sub-benefit (e.g. "$10 – Qty:
// 2" DoorDash Grocery under DoorDash) is edited in credits, like the table's
// add popover records it (SUPPORT-930).
const editingCountBasedSubBenefit = computed(() => {
    const uuid = props.editingEntry?.uuid;
    if (!uuid) return null;
    const sub = (selectedBenefit.value?.sub_benefits || []).find((s) =>
        (s.usage_records || []).some((r) => r.uuid === uuid)
    );
    return sub?.values_summary?.is_count_based ? sub : null;
});

// Check if this is a count-based benefit
const isCountBased = computed(() => {
    if (editingCountBasedSubBenefit.value) return true;
    const benefit = selectedBenefit.value?.benefit;
    return store.checkIsCountBased(benefit);
});

// By-count benefits that still carry a dollar value (e.g. 2 × $250): the user
// enters a $ amount (isCountBased is false), but the meaningful "remaining"
// unit is the number of uses, capped at value_details.count by the backend
// ValidateBenefitUsageCount rule.
const isByCountValueBenefit = computed(() => {
    if (isCountBased.value) {
        return false;
    }
    const count =
        parseFloat(selectedBenefit.value?.benefit?.value_details?.count) || 1;
    return count > 1;
});

const countLabel = computed(() => {
    const source =
        editingCountBasedSubBenefit.value || selectedBenefit.value?.benefit;
    return source?.value_details?.count_label || '';
});

const maxCount = computed(() => {
    const source =
        editingCountBasedSubBenefit.value || selectedBenefit.value?.benefit;
    return source?.value_details?.count || 0;
});

// Get the version that was effective at the selected date
const effectiveVersionForSelectedDate = computed(() => {
    const benefit = selectedBenefit.value;
    if (!benefit || !model.value.date) return null;

    const versionHistory = benefit.version_history || [];
    if (versionHistory.length === 0) return null;

    const selectedDate = dayjs(model.value.date);

    // Find the version that was effective at the selected date
    const exactMatch = versionHistory.find((version) => {
        const effectiveStart = dayjs(version.effective_start);
        const effectiveEnd = version.effective_end
            ? dayjs(version.effective_end)
            : null;

        // Check if selected date is within this version's effective period
        const afterStart =
            selectedDate.isSame(effectiveStart, 'day') ||
            selectedDate.isAfter(effectiveStart);
        const beforeEnd = !effectiveEnd || selectedDate.isBefore(effectiveEnd);

        return afterStart && beforeEnd;
    });

    if (exactMatch) return exactMatch;

    // If no exact match, check if the date is before the earliest version
    // In this case, use the earliest version (assumes benefit existed before versioning was implemented)
    const sortedVersions = [...versionHistory].sort(
        (a, b) =>
            dayjs(a.effective_start).valueOf() -
            dayjs(b.effective_start).valueOf()
    );
    const earliestVersion = sortedVersions[0];

    if (
        earliestVersion &&
        selectedDate.isBefore(dayjs(earliestVersion.effective_start))
    ) {
        return earliestVersion;
    }

    return null;
});

// Check if the selected date has an active version
const hasActiveVersionForSelectedDate = computed(() => {
    return effectiveVersionForSelectedDate.value !== null;
});

// Display amount for the selected date (handles version history and monthly overrides)
const displayAmountForSelectedDate = computed(() => {
    const benefit = selectedBenefit.value;
    if (!benefit) return 0;

    // First, check version history for the selected date
    const effectiveVersion = effectiveVersionForSelectedDate.value;

    // If there's version history but no version is active for this date, return 0
    if (benefit.version_history?.length > 0 && !effectiveVersion) {
        return 0;
    }

    // Use the effective version's amount if available, otherwise fall back to current benefit amount
    const baseAmount = effectiveVersion
        ? parseFloat(effectiveVersion.amount) || 0
        : parseFloat(benefit.benefit?.amount) || 0;

    const valueDetails =
        effectiveVersion?.value_details || benefit.benefit?.value_details || {};
    const periodType =
        effectiveVersion?.period_type || benefit.benefit?.period_type;

    // For monthly benefits, check if selected date's month matches override month
    if (periodType?.toLowerCase() === 'monthly') {
        const overrideMonth = valueDetails.override_month?.month;
        const overrideValue = valueDetails.override_month?.value;

        if (overrideMonth && overrideValue != null && model.value.date) {
            // Use dayjs to avoid timezone issues when extracting month
            const selectedMonth = String(
                dayjs(model.value.date).month() + 1
            ).padStart(2, '0');

            if (overrideMonth === selectedMonth) {
                let periodValue = parseFloat(overrideValue) || 0;
                const count = parseFloat(valueDetails.count) || 1;
                if (count > 1) {
                    periodValue *= count;
                }
                return periodValue;
            }
        }

        return baseAmount;
    }

    return baseAmount;
});

// Calculate usage for the selected period
const usageForSelectedPeriod = computed(() => {
    if (!model.value.date || !selectedBenefit.value) return 0;

    const periodType = selectedBenefit.value.benefit?.period_type;
    const selectedPeriodKey = store.getPeriodKey(model.value.date, periodType);
    const currentPeriodKey = store.getPeriodKey(new Date(), periodType);

    // If selected period is current period, use current_period_usage
    if (selectedPeriodKey === currentPeriodKey) {
        return selectedBenefit.value.current_period_usage || 0;
    }

    // Otherwise, calculate usage from usage_records for that period
    const usageRecords = selectedBenefit.value.usage_records || [];
    return usageRecords
        .filter((record) => {
            const recordPeriodKey = store.getPeriodKey(
                record.used_at,
                periodType
            );
            return recordPeriodKey === selectedPeriodKey;
        })
        .reduce(
            (sum, record) => sum + (parseFloat(record.total_amount_used) || 0),
            0
        );
});

// Calculate remaining amount considering the entry being edited
const remainingAmount = computed(() => {
    const benefitAmount = displayAmountForSelectedDate.value;
    const usedAmount = usageForSelectedPeriod.value;

    // If editing, subtract the original amount from usage to get the "available" amount
    const originalAmount =
        isEditing.value && props.editingEntry?.total_amount_used
            ? parseFloat(props.editingEntry.total_amount_used)
            : 0;

    const availableAmount = benefitAmount - (usedAmount - originalAmount);
    return Math.max(0, availableAmount).toFixed(2);
});

const PERIOD_LENGTH_IN_MONTHS = {
    MONTHLY: 1,
    QUARTERLY: 3,
    SEMI_ANNUAL: 6,
    ANNUAL: 12,
    BIENNIAL: 24,
    TRIENNIAL: 36,
    QUADRENNIAL: 48,
    QUINQUENNIAL: 60,
};

// The editing sub-benefit's current period as the server computed it: it ends
// on period_end_date and spans one period length. Null for statement/lifetime
// periods or when the server sent no end date.
const subCurrentPeriodWindow = computed(() => {
    const sub = editingCountBasedSubBenefit.value;
    const end = sub?.period_end_date || sub?.usage?.period_end_date;
    const months = PERIOD_LENGTH_IN_MONTHS[String(sub?.period_type || '').toUpperCase()];
    if (!end || !months) return null;
    const periodEnd = dayjs(end);
    return { start: periodEnd.add(1, 'day').subtract(months, 'month'), end: periodEnd };
});

const isInWindow = (date, periodWindow) =>
    !!date &&
    !dayjs(date).isBefore(periodWindow.start, 'day') &&
    !dayjs(date).isAfter(periodWindow.end, 'day');

// Calculate remaining count for count-based benefits
const remainingCount = computed(() => {
    const maxUses = maxCount.value;

    // Count-based sub-benefit: credits the other records use in the current
    // period, so the edited record's own credits stay editable. The period
    // window comes from the server's period_end_date, which follows anniversary
    // resets (calendar grouping does not); the records are the store's live
    // list, which an add right before this edit has already updated. Outside
    // the current period the server's count rule is the only check.
    const sub = editingCountBasedSubBenefit.value;
    if (sub) {
        const periodWindow = subCurrentPeriodWindow.value;
        if (!periodWindow || !isInWindow(model.value.date, periodWindow)) {
            return maxUses;
        }
        const usedByOtherRecords = (sub.usage_records || [])
            .filter(
                (record) =>
                    record.uuid !== props.editingEntry?.uuid &&
                    isInWindow(record.used_at, periodWindow)
            )
            .reduce((sum, record) => sum + (parseInt(record.usage_count) || 1), 0);
        return Math.max(0, maxUses - usedByOtherRecords);
    }

    // By-count benefits with a dollar value: each record is one use. Count
    // records in the selected period, excluding the entry being edited
    // (parallels how remainingAmount subtracts the edited record's amount).
    // Counting records — rather than summing usage_count — keeps historical
    // periods correct, since records logged before the benefit became by-count
    // carry usage_count = 0.
    if (isByCountValueBenefit.value) {
        const periodType = selectedBenefit.value?.benefit?.period_type;
        const selectedPeriodKey = store.getPeriodKey(
            model.value.date,
            periodType
        );
        const editingId =
            props.editingEntry?.uuid ?? props.editingEntry?.id ?? null;
        const usedRecords = (
            selectedBenefit.value?.usage_records || []
        ).filter(
            (record) =>
                store.getPeriodKey(record.used_at, periodType) ===
                    selectedPeriodKey &&
                (record.uuid ?? record.id) !== editingId
        ).length;
        return Math.max(0, maxUses - usedRecords);
    }

    const currentUsed = selectedBenefit.value?.current_period_usage || 0;
    return Math.max(0, maxUses - currentUsed);
});

// Per-transaction cap for by-count benefits (value_details.count > 1): a single
// entry can't exceed the version's per-use amount even when the period still
// has room (e.g. a 2 × $250 credit caps each entry at $250 though $500 remains).
// Mirrors the backend ValidateBenefitUsagePerTransactionAmount rule. Computed
// directly from the per-unit amount — not displayAmountForSelectedDate / count,
// since that helper here returns the per-unit value for non-override months —
// honouring a matching monthly override-month value. Infinity (no cap) for
// single-allotment benefits.
const perTransactionMaxAmount = computed(() => {
    const benefit = selectedBenefit.value;
    const effectiveVersion = effectiveVersionForSelectedDate.value;
    const valueDetails =
        effectiveVersion?.value_details || benefit?.benefit?.value_details || {};
    const count = parseFloat(valueDetails.count) || 1;
    if (count <= 1) return Infinity;

    const periodType =
        effectiveVersion?.period_type || benefit?.benefit?.period_type;
    if (periodType?.toLowerCase() === 'monthly') {
        const overrideMonth = valueDetails.override_month?.month;
        const overrideValue = valueDetails.override_month?.value;
        if (overrideMonth && overrideValue != null && model.value.date) {
            const selectedMonth = String(
                dayjs(model.value.date).month() + 1
            ).padStart(2, '0');
            if (overrideMonth === selectedMonth) {
                return parseFloat(overrideValue) || 0;
            }
        }
    }

    return effectiveVersion
        ? parseFloat(effectiveVersion.amount) || 0
        : parseFloat(benefit?.benefit?.amount) || 0;
});

const isFormValid = computed(() => {
    if (isCountBased.value) {
        return model.value.count && model.value.date && !loading.value;
    }
    return model.value.amount && model.value.date && !loading.value;
});

// Validation method
const validateForm = () => {
    errors.value = {};

    if (!model.value.date) {
        errors.value.usageDate = 'Date used is required';
        return false;
    }

    // Check if there's an active version for the selected date
    if (
        selectedBenefit.value?.version_history?.length > 0 &&
        !hasActiveVersionForSelectedDate.value
    ) {
        errors.value.usageDate = 'No benefit version was active on this date';
        return false;
    }

    if (isCountBased.value) {
        // Count validation
        if (!model.value.count) {
            errors.value.usageCount = 'Quantity is required';
            return false;
        }

        const count = parseInt(model.value.count);
        if (count <= 0) {
            errors.value.usageCount = 'Quantity must be greater than 0';
            return false;
        } else if (count > remainingCount.value) {
            errors.value.usageCount = `Quantity cannot exceed remaining ${countLabel.value || 'uses'} of ${remainingCount.value}`;
            return false;
        }
    } else {
        // Amount validation
        if (!model.value.amount) {
            errors.value.usageAmount = 'Usage amount is required';
            return false;
        }

        const amount = parseFloat(model.value.amount);
        if (amount <= 0) {
            errors.value.usageAmount = 'Usage amount must be greater than 0';
            return false;
        }

        const effectiveMaxAmount = Math.min(
            parseFloat(remainingAmount.value),
            perTransactionMaxAmount.value
        );
        if (amount > effectiveMaxAmount) {
            errors.value.usageAmount = isByCountValueBenefit.value
                ? `Usage amount cannot exceed $${effectiveMaxAmount.toFixed(2)} per use`
                : `Usage amount cannot exceed remaining value of $${remainingAmount.value}`;
            return false;
        }

        // By-count benefits are also capped on the number of uses per period
        // (mirrors backend ValidateBenefitUsageCount).
        if (isByCountValueBenefit.value && remainingCount.value < 1) {
            errors.value.usageAmount = 'No remaining uses this period';
            return false;
        }
    }

    return true;
};

const handleSave = async () => {
    if (!validateForm()) return;

    loading.value = true;

    try {
        let success;

        if (isEditing.value) {
            // Update existing usage record directly
            success = await store.handleUsageUpdate(
                model.value,
                selectedBenefit.value,
                props.editingEntry
            );
        } else {
            // Create new usage record
            success = await store.handleUsageSubmit(
                model.value,
                selectedBenefit.value
            );

            if (success) {
                emit('usage-saved', model.value);
            }
        }

        if (success) {
            closeModal();
        }
    } finally {
        loading.value = false;
    }
};

const closeModal = () => {
    emit('close');
    errors.value = {};
    loading.value = false;
};
</script>
